/**
 * KRA Photo Journal – nối bộ ảnh cũ với bản copy trên Drive admin
 *
 * Chiều xuôi (record → Drive) chỉ chạy được với bộ đã lưu `adminDriveLink`.
 * Hàng trăm bộ cũ được copy vào Drive bằng tay nên record không giữ ID nào,
 * và app không có cách nào biết chúng nằm trong "08. Families" hay đâu.
 *
 * Ở đây đi chiều ngược: lập chỉ mục cây `01. Kimono` trên Drive (danh mục →
 * folder từng bộ), rồi khớp tên folder với tên bộ trong app. Khớp thì ghi cả
 * `adminDriveLink` lẫn `adminCategoryFolder`.
 *
 * Chỉ khớp tuyệt đối sau khi chuẩn hoá — không fuzzy. Tên nào xuất hiện ở hai
 * folder khác nhau thì bỏ qua luôn, thà sót còn hơn gắn nhầm bộ của khách này
 * vào folder của khách khác.
 */

import {
  listSubfolders,
  getFolderWithParentName,
  extractFolderIdFromUrl,
  folderUrl,
} from "../hooks/useGoogleDrive.js";
import { fetchPhotos, fetchDriveFolders, patchPhoto } from "./api.js";

export type LinkScanPhase = "idle" | "root" | "index" | "scan" | "write";

export interface LinkScanProgress {
  phase: LinkScanPhase;
  /** Folder bộ đã lập chỉ mục trên Drive */
  indexed: number;
  /** Bộ trong app đang thiếu link Drive admin */
  candidates: number;
  written: number;
  unmatched: number;
}

export interface LinkScanResult extends LinkScanProgress {
  stopped: boolean;
  /** Tên cây thư mục gốc đã dò ra, để hiện lại cho admin */
  rootName: string;
}

/** Hậu tố Google Takeout dán vào tên folder khi tải về rồi upload lại. */
const TAKEOUT_SUFFIX = /-\d{8}t\d{6}z-\d{3}$/;

/** Chuẩn hoá tên để so khớp: bỏ dấu, bỏ hậu tố Takeout, gộp khoảng trắng. */
export function normaliseSetName(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    // đ/Đ là chữ cái riêng, NFD không tách ra được — "Gia đình" vs "Gia dinh"
    .replace(/\u0111/g, "d")
    .replace(/\s+/g, " ")
    .trim()
    .replace(TAKEOUT_SUFFIX, "")
    .trim();
}

/** Airtable cho ~5 lượt ghi/giây mỗi base — giữ khoảng cách an toàn. */
const WRITE_DELAY_MS = 220;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Dò ID của cây thư mục gốc (`01. Kimono`) từ chính dữ liệu đã có: lấy một bộ
 * đã gắn folder → cha của nó là danh mục → cha của danh mục là gốc.
 * Không bắt admin phải đi chọn thư mục.
 */
async function resolveRoot(token: string): Promise<{ id: string; name: string }> {
  const folders = await fetchDriveFolders();
  const categories = folders
    .filter((f) => /^\d+\s*\./.test(f.name.trim()))
    .sort((a, b) => b.count - a.count);

  for (const cat of categories.slice(0, 3)) {
    const page = await fetchPhotos({ folder: cat.name, pageSize: 5 });
    for (const rec of page.records) {
      const setId = extractFolderIdFromUrl(rec.adminDriveLink ?? "");
      if (!setId) continue;
      const meta = await getFolderWithParentName(setId, token);
      // Bộ nào link trỏ vào thư mục con lồng bên trong thì cha không phải danh
      // mục — bỏ qua, lấy bộ khác.
      if (!meta.parentId || meta.parentName?.trim() !== cat.name) continue;
      const catMeta = await getFolderWithParentName(meta.parentId, token);
      if (catMeta.parentId) {
        return { id: catMeta.parentId, name: catMeta.parentName ?? "thư mục gốc" };
      }
    }
  }
  throw new Error(
    "Không dò được cây thư mục gốc trên Drive. Chạy “Điền folder cho bộ cũ” trước đã.",
  );
}

export interface LinkScanOptions {
  token: string;
  onProgress: (p: LinkScanProgress) => void;
  shouldStop: () => boolean;
}

export async function runLinkScan({
  token,
  onProgress,
  shouldStop,
}: LinkScanOptions): Promise<LinkScanResult> {
  const progress: LinkScanProgress = {
    phase: "root", indexed: 0, candidates: 0, written: 0, unmatched: 0,
  };
  const emit = () => onProgress({ ...progress });
  emit();

  const root = await resolveRoot(token);

  // ── 1. Lập chỉ mục folder bộ trên Drive ────────────────────────────────────
  progress.phase = "index";
  emit();

  // null = tên trùng ở nhiều nơi → không dám khớp
  const index = new Map<string, { folderId: string; category: string } | null>();
  const categories = await listSubfolders(root.id, token);

  for (const cat of categories) {
    if (shouldStop()) break;
    const sets = await listSubfolders(cat.id, token);
    for (const s of sets) {
      const key = normaliseSetName(s.name);
      if (!key) continue;
      if (index.has(key)) index.set(key, null);
      else index.set(key, { folderId: s.id, category: cat.name });
      progress.indexed++;
    }
    emit();
  }

  // ── 2. Gom các bộ trong app còn thiếu link Drive admin ────────────────────
  progress.phase = "scan";
  emit();

  const targets: Array<{ id: string; chatName: string }> = [];
  let offset: string | undefined;
  do {
    if (shouldStop()) break;
    const page = await fetchPhotos({ pageSize: 100, ...(offset ? { offset } : {}) });
    for (const rec of page.records) {
      if (!(rec.adminDriveLink ?? "").trim() && rec.chatName?.trim()) {
        targets.push({ id: rec.id, chatName: rec.chatName });
      }
    }
    progress.candidates = targets.length;
    emit();
    offset = page.offset ?? undefined;
  } while (offset);

  // ── 3. Khớp tên rồi ghi ───────────────────────────────────────────────────
  progress.phase = "write";
  emit();

  // Bảo vệ chiều ngược lại: hai bộ khác nhau trong app cùng mang tên
  // "Gia đình 5 người" thì không được cùng trỏ vào một folder — bỏ qua cả hai.
  const appNameCount = new Map<string, number>();
  for (const t of targets) {
    const k = normaliseSetName(t.chatName);
    appNameCount.set(k, (appNameCount.get(k) ?? 0) + 1);
  }

  for (const t of targets) {
    if (shouldStop()) break;
    const key = normaliseSetName(t.chatName);
    const hit = (appNameCount.get(key) ?? 0) > 1 ? null : index.get(key);
    if (!hit) {
      progress.unmatched++;
      emit();
      continue;
    }
    try {
      await patchPhoto(t.id, {
        adminDriveLink: folderUrl(hit.folderId),
        adminCategoryFolder: hit.category,
      });
      progress.written++;
    } catch {
      progress.unmatched++;
    }
    emit();
    await sleep(WRITE_DELAY_MS);
  }

  return { ...progress, phase: "idle", stopped: shouldStop(), rootName: root.name };
}
